/**
 * §A.3 · «¿Le llegó el aviso?»
 *
 * ## Es la llamada más frecuente que vamos a recibir
 *
 * «No me llegó el correo del recibo.» El contrato la nombra así y le pone la
 * condición que da forma a esta pantalla: **se resuelve sin abrir un modo soporte**,
 * que es la fricción que hay que reservar para cuando de verdad hay que mirar datos
 * de un menor.
 *
 * Así que se entra por «solo lo que falló», que es con lo que se empieza, y el filtro
 * por correo es lo segundo. Lo que no hay es una lista completa de todo lo enviado
 * por defecto: con doscientos clubes son miles de filas al día y ninguna es la que se
 * busca.
 *
 * ## Aquí no se lee el cuerpo de nada
 *
 * El DTO no trae `body` y la consulta del servidor no lo selecciona: «para un aviso
 * de recibo el cuerpo sería inocuo; para un mensaje de chat es el contenido de una
 * conversación entre una madre y un entrenador, que no leemos». Se dice en pantalla
 * porque es la primera pregunta de quien viene buscando «qué le pusieron».
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import { estadoDeTraza, type TrazaDeAviso } from '@/backoffice/tipos';

export function TrazasDeAvisos() {
  const [soloFallos, setSoloFallos] = useState(true);
  const [correo, setCorreo] = useState('');

  const trazas = useQuery({
    queryKey: ['admin', 'avisos', soloFallos],
    queryFn: async () => {
      const { data, error } = await api.v1.admin.notifications.get({
        query: { ...(soloFallos ? { only_failed: true } : {}), limit: 200 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TrazaDeAviso[];
    },
  });

  /*
   * El filtro por correo es **del lado del cliente**, y a propósito: la ruta filtra
   * por `user_id`, no por correo, y quien atiende el teléfono tiene un correo y no un
   * uuid. Buscar la persona primero en «Personas» para copiar su id sería dar dos
   * pasos para resolver una llamada de treinta segundos.
   */
  const filtradas = (trazas.data ?? []).filter((t) =>
    correo.trim() === ''
      ? true
      : (t.user_email ?? '').toLowerCase().includes(correo.trim().toLowerCase())
  );

  return (
    <section>
      <h1>¿Le llegó el aviso?</h1>

      <p className="tenue">
        Estado de envío por persona y tipo.{' '}
        <strong>Aquí no se lee el contenido de nada</strong>: de un mensaje de
        chat solo viaja que existió, no lo que decía.
      </p>

      <div className="filtros">
        <label>
          Correo de la persona
          <input
            type="search"
            placeholder="madre@example.es"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
        </label>
        <label>
          Qué se ve
          <select
            value={soloFallos ? 'fallos' : 'todo'}
            onChange={(e) => setSoloFallos(e.target.value === 'fallos')}
          >
            <option value="fallos">Solo lo que no salió o falló</option>
            <option value="todo">Todo lo enviado</option>
          </select>
        </label>
      </div>

      {trazas.isPending && (
        <Cargando filas={5} etiqueta="Cargando los avisos" />
      )}

      {trazas.isError && (
        <PanelError
          problema={aProblema(trazas.error)}
          reintentar={() => void trazas.refetch()}
        />
      )}

      {trazas.data &&
        (filtradas.length === 0 ? (
          <Vacio
            titulo={soloFallos ? 'No ha fallado ningún aviso' : 'No hay avisos'}
            explicacion={
              soloFallos
                ? 'Es la buena noticia. Si alguien dice que no le llegó, mira en «Todo lo enviado»: puede que saliera y no lo haya visto.'
                : 'Nada enviado todavía con estos filtros.'
            }
          />
        ) : (
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>A quién</th>
                  <th>Qué</th>
                  <th>Por dónde</th>
                  <th>Estado</th>
                  <th>Si falló, por qué</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((t) => {
                  const e = estadoDeTraza(t);
                  return (
                    <tr key={t.id}>
                      <td>{fechaYHora(t.created_at)}</td>
                      <td>{t.user_email ?? '—'}</td>
                      <td>
                        {t.title}
                        <br />
                        <code className="tenue">{t.type}</code>
                      </td>
                      <td className="tenue">{t.channel}</td>
                      <td>
                        <span className={`pastilla ${e.clase}`}>
                          {e.etiqueta}
                        </span>
                      </td>
                      <td className="tenue">
                        {/*
                          El motivo tal cual lo da el proveedor: «mailbox full»,
                          «invalid token». Reescribirlo sería perder lo único que
                          permite decirle a la familia qué arreglar.
                        */}
                        {t.failed_reason ?? '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}
